import { Body2 } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations/content/typed-content";
import { thousandSeparator } from "@iventis/utilities";
import React, { FunctionComponent } from "react";
import { styled } from "@iventis/styles";
import { useIventisTranslate, UseIventisTranslate } from "@iventis/translations/use-iventis-translate";

type fruinType = "flowRate" | "capacity";

type fruinServiceLevels = {
    A: string;
    B: string;
    C: string;
    D: string;
    E: string;
    F: string;
};

/**
 * Determines the capcity given an area and a occpancy index.
 * @param {number} area - Area that requires capacity calucation. (unit^2)
 * @param {number} occupancyIndex - Occupancy Index (unit^2/person)
 * @return {number} Capacity (number of people)
 */
export const calcCapacity = (area: number, occupancyIndex: number): number => Math.floor(area / occupancyIndex);

export const calcFlowRate = (ppm: number, distance: number): number => Math.floor(ppm * distance);

const fruinFlowrateLevels = {
    A: 23,
    B: 33,
    C: 49,
    D: 66,
    E: 82,
    F: 82,
};

const flowRatefruinServiceLevels = (translate: UseIventisTranslate, length: number) => {
    const format = (values: { perMinute: number; perHour: number }) =>
        translate(Content.map2.analysis.flow_rate_content.per_minute_per_hour, { perMinute: thousandSeparator(values.perMinute), perHour: thousandSeparator(values.perHour) });
    return {
        A: format({ perMinute: calcFlowRate(fruinFlowrateLevels.A, length), perHour: calcFlowRate(fruinFlowrateLevels.A, length) * 60 }),
        B: format({ perMinute: calcFlowRate(fruinFlowrateLevels.B, length), perHour: calcFlowRate(fruinFlowrateLevels.B, length) * 60 }),
        C: format({ perMinute: calcFlowRate(fruinFlowrateLevels.C, length), perHour: calcFlowRate(fruinFlowrateLevels.C, length) * 60 }),
        D: format({ perMinute: calcFlowRate(fruinFlowrateLevels.D, length), perHour: calcFlowRate(fruinFlowrateLevels.D, length) * 60 }),
        E: format({ perMinute: calcFlowRate(fruinFlowrateLevels.E, length), perHour: calcFlowRate(fruinFlowrateLevels.E, length) * 60 }),
        F: format({ perMinute: calcFlowRate(fruinFlowrateLevels.F, length), perHour: calcFlowRate(fruinFlowrateLevels.F, length) * 60 }),
    };
};

const fruinCapacityLevels = {
    A: 3.25,
    B: { min: 3.25, max: 2.32 },
    C: { min: 2.32, max: 1.39 },
    D: { min: 1.39, max: 0.93 },
    E: { min: 0.93, max: 0.46 },
    F: 0.46,
};

export const capacityfruinServiceLevels = (area: number) => {
    const format = (value: { min: number; max: number } | number) =>
        typeof value === "number" ? thousandSeparator(value) : `${thousandSeparator(value.min)} - ${thousandSeparator(value.max)}`;
    return {
        A: `< ${format(calcCapacity(area, fruinCapacityLevels.A))}`,
        B: format({ min: calcCapacity(area, fruinCapacityLevels.B.min), max: calcCapacity(area, fruinCapacityLevels.B.max) }),
        C: format({ min: calcCapacity(area, fruinCapacityLevels.C.min), max: calcCapacity(area, fruinCapacityLevels.C.max) }),
        D: format({ min: calcCapacity(area, fruinCapacityLevels.D.min), max: calcCapacity(area, fruinCapacityLevels.D.max) }),
        E: format({ min: calcCapacity(area, fruinCapacityLevels.E.min), max: calcCapacity(area, fruinCapacityLevels.E.max) }),
        F: `> ${format(calcCapacity(area, fruinCapacityLevels.F))}`,
    };
};

const fruinColours: fruinServiceLevels = {
    A: "#00A3FF",
    B: "#0700c4",
    C: "#008000",
    D: "#FBEC5D",
    E: "#F0A150",
    F: "#FF1919",
};

export const FruinServiceLevelsComponent: FunctionComponent<{ fruinType: fruinType; magnitude: number }> = ({ fruinType, magnitude }) => {
    const translate = useIventisTranslate();

    const fruinContent: Record<fruinType, fruinServiceLevels> = {
        flowRate: flowRatefruinServiceLevels(translate, magnitude),
        capacity: capacityfruinServiceLevels(magnitude),
    };

    return (
        <>
            {Object.entries(fruinColours).map(([key, colour]) => (
                <StyledfruinRow key={key}>
                    <div className="point" style={{ backgroundColor: colour }} />
                    <Body2>{key}</Body2>
                    <Body2>{fruinContent[fruinType][key]}</Body2>
                </StyledfruinRow>
            ))}
        </>
    );
};

const StyledfruinRow = styled.div`
    display: flex;
    gap: 5px;
    .point {
        width: 10px;
        height: 10px;
        border-radius: 50%;
    }
`;
